/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { createAction, createSlice, createSelector } from '@reduxjs/toolkit';
import fromPairs from 'lodash/fromPairs';

import createRequestActionTypes from 'store/helpers/createRequestActionTypes';
import {
  handleClearErrors,
  onFailure,
  onSuccessList,
} from 'store/helpers/commonHandlers';
import { PREPARE_PAYMENT_SUCCESS } from 'features/payments';
import { ASSIGN_FORMSET_SUCCESS } from 'features/forms';
import getSplicedList from 'lib/utils/getUpdatedList';

const name = 'reservations';

/** ActionType for Request */
export const [
  GET_RESERVATION_LIST,
  GET_RESERVATION_LIST_SUCCESS,
  GET_RESERVATION_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_RESERVATION_LIST');
export const [
  GET_WEEKLY_RESERVATION_LIST,
  GET_WEEKLY_RESERVATION_LIST_SUCCESS,
  GET_WEEKLY_RESERVATION_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_WEEKLY_RESERVATION_LIST');
export const [
  GET_TODAY_RESERVATION_LIST,
  GET_TODAY_RESERVATION_LIST_SUCCESS,
  GET_TODAY_RESERVATION_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_TODAY_RESERVATION_LIST');
export const [
  GET_NOT_PAID_RESERVATION_LIST,
  GET_NOT_PAID_RESERVATION_LIST_SUCCESS,
  GET_NOT_PAID_RESERVATION_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_NOT_PAID_RESERVATION_LIST');
export const [
  GET_NOT_ASSIGNED_RESERVATION_LIST,
  GET_NOT_ASSIGNED_RESERVATION_LIST_SUCCESS,
  GET_NOT_ASSIGNED_RESERVATION_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_NOT_ASSIGNED_RESERVATION_LIST');
export const [
  GET_PATIENT_RESERVATION_LIST,
  GET_PATIENT_RESERVATION_LIST_SUCCESS,
  GET_PATIENT_RESERVATION_LIST_FAILURE,
] = createRequestActionTypes(name, 'GET_PATIENT_RESERVATION_LIST');
export const [
  CHECK_RESERVATIONS,
  CHECK_RESERVATIONS_SUCCESS,
  CHECK_RESERVATIONS_FAILURE,
] = createRequestActionTypes(name, 'CHECK_RESERVATIONS');
export const [
  CREATE_RESERVATION,
  CREATE_RESERVATION_SUCCESS,
  CREATE_RESERVATION_FAILURE,
] = createRequestActionTypes(name, 'CREATE_RESERVATION');
export const [
  UPDATE_RESERVATION,
  UPDATE_RESERVATION_SUCCESS,
  UPDATE_RESERVATION_FAILURE,
] = createRequestActionTypes(name, 'UPDATE_RESERVATION');
export const [
  DELETE_RESERVATION,
  DELETE_RESERVATION_SUCCESS,
  DELETE_RESERVATION_FAILURE,
] = createRequestActionTypes(name, 'DELETE_RESERVATION');

const slice = createSlice({
  name,
  initialState: {
    list: null,
    weeklyList: null,
    todayList: null,
    notPaidList: null,
    notAssignedList: null,
    listByPatient: {},
    errors: {},
    count: {},
    next: {},
  },
  reducers: {
    clearErrors: handleClearErrors,
  },
  extraReducers: {
    [GET_RESERVATION_LIST_SUCCESS]: onSuccessList('list'),
    [GET_RESERVATION_LIST_FAILURE]: onFailure('list'),
    [GET_TODAY_RESERVATION_LIST_SUCCESS]: onSuccessList('todayList'),
    [GET_TODAY_RESERVATION_LIST_FAILURE]: onFailure('todayList'),
    [GET_WEEKLY_RESERVATION_LIST_SUCCESS]: onSuccessList('weeklyList'),
    [GET_WEEKLY_RESERVATION_LIST_FAILURE]: onFailure('weeklyList'),
    [GET_NOT_PAID_RESERVATION_LIST_SUCCESS]: onSuccessList('notPaidList'),
    [GET_NOT_PAID_RESERVATION_LIST_FAILURE]: onFailure('notPaidList'),
    [GET_NOT_ASSIGNED_RESERVATION_LIST_SUCCESS]: onSuccessList(
      'notAssignedList',
    ),
    [GET_NOT_ASSIGNED_RESERVATION_LIST_FAILURE]: onFailure('notAssignedList'),
    [GET_PATIENT_RESERVATION_LIST_SUCCESS]: (
      state,
      { payload: { results, next, count } },
    ) => {
      if (results?.length) {
        const firstRes = results[0];
        state.listByPatient = {
          ...state.listByPatient,
          [firstRes.patient.id]: results,
        };
      }
    },
    [UPDATE_RESERVATION_SUCCESS]: (state, { payload }) => {
      state.list = getSplicedList(state.list, payload);
      state.weeklyList = getSplicedList(state.weeklyList, payload);
      state.todayList = getSplicedList(state.todayList, payload);
      state.notPaidList = getSplicedList(state.notPaidList, payload);
      state.notAssignedList = getSplicedList(state.notAssignedList, payload);
    },
    [UPDATE_RESERVATION_FAILURE]: onFailure('updateReservation'),
    [DELETE_RESERVATION_SUCCESS]: (state, { payload }) => {
      state.list = getSplicedList(state.list, payload, { exchange: false });
      state.weeklyList = getSplicedList(state.weeklyList, payload, {
        exchange: false,
      });
      state.todayList = getSplicedList(state.todayList, payload, {
        exchange: false,
      });
      state.notPaidList = getSplicedList(state.notPaidList, payload, {
        exchange: false,
      });
      state.notAssignedList = getSplicedList(state.notAssignedList, payload, {
        exchange: false,
      });
      state.listByPatient = fromPairs(
        Object.entries(state.listByPatient).map(([k, list]) => {
          const nextList = getSplicedList(list, payload, { exchange: false });
          return [k, nextList];
        }),
      );
    },
    // Other
    [PREPARE_PAYMENT_SUCCESS]: (state, { payload }) => {
      if (state.notPaidList) {
        const nextList = [...state.notPaidList];
        const idx = nextList.findIndex((o) => o.id === payload.reservation);
        if (idx !== -1) {
          nextList.splice(idx, 1);
        }
        state.notPaidList = nextList;
      }
    },
    [CREATE_RESERVATION_FAILURE]: onFailure('createReservation'),
    [ASSIGN_FORMSET_SUCCESS]: (state, { payload }) => {
      if (state.notAssignedList) {
        const nextList = [...state.notAssignedList];
        const idx = nextList.findIndex((o) => o.id === payload.reservation);
        if (idx !== -1) {
          nextList.splice(idx, 1);
        }
        state.notAssignedList = nextList;
      }
    },
  },
});

/** Action for Request */
export const getReservationList = createAction(GET_RESERVATION_LIST);
export const getTodayReservationList = createAction(GET_TODAY_RESERVATION_LIST);
export const getWeeklyReservationList = createAction(
  GET_WEEKLY_RESERVATION_LIST,
);
export const getNotPaidReservationList = createAction(
  GET_NOT_PAID_RESERVATION_LIST,
);
export const getNotAssignedReservationList = createAction(
  GET_NOT_ASSIGNED_RESERVATION_LIST,
);
export const getPatientReservationList = createAction(
  GET_PATIENT_RESERVATION_LIST,
);
export const checkReservations = createAction(CHECK_RESERVATIONS);
export const createReservation = createAction(CREATE_RESERVATION);
export const updateReservation = createAction(UPDATE_RESERVATION);
export const deleteReservation = createAction(DELETE_RESERVATION);

/** General Action */
export const { clearErrors } = slice.actions;

/** Reducer */
export const reservations = slice.reducer;

/** Selectors */
const errorSelector = createSelector(
  (state) => state.errors,
  (errors) => ({ errors }),
);
const listSelector = createSelector(
  (state) => state.list,
  (state) => state.count.list,
  (reservationList, reservationCount) => ({
    reservationList,
    reservationCount,
  }),
);
const todayListSelector = createSelector(
  (state) => state.todayList,
  (state) => {
    const all = state.count.todayList;
    let notCounted = 0;
    if (state.todayList) {
      notCounted = state.todayList.filter((r) => r.is_canceled || r.is_deleted)
        .length;
    }
    return (all || 0) - notCounted;
  },
  (todayList, todayCount) => ({
    todayList,
    todayCount,
  }),
);
const weeklySelector = createSelector(
  (state) => state.weeklyList,
  (state) => state.count.weeklyList,
  (weeklyList, weeklyListCount) => ({
    weeklyList,
    weeklyListCount,
  }),
);
const notPaidListSelector = createSelector(
  (state) => state.notPaidList,
  (state) => state.count.notPaidList,
  (notPaidList, notPaidCount) => ({ notPaidList, notPaidCount }),
);
const notAssignedListSelector = createSelector(
  (state) => state.notAssignedList,
  (state) => state.count.notAssignedList,
  (notAssignedList, notAssignedListCount) => ({
    notAssignedList,
    notAssignedListCount,
  }),
);
const patientReservationSelector = createSelector(
  (state) => state.listByPatient,
  (listByPatient) => ({
    listByPatient,
  }),
);
export const selectors = {
  errors: (state) => errorSelector(state[name]),
  list: (state) => listSelector(state[name]),
  today: (state) => todayListSelector(state[name]),
  weekly: (state) => weeklySelector(state[name]),
  notPaidList: (state) => notPaidListSelector(state[name]),
  notAssignedList: (state) => notAssignedListSelector(state[name]),
  listByPatient: (state) => patientReservationSelector(state[name]),
};

export default slice;
